import {Button, Typography, useMediaQuery} from '@material-ui/core';
import AccountRouteConstants from 'Agent/Accounts/AccountRouteConstants';
import {FilterEnum} from 'Agent/Accounts/ducks/account';
import {changeApplicationsFilter, getApplications, changeSort} from 'Agent/Policies/ducks/policyApplications';
import {useBreadcrumbs} from 'Core/hooks/useBreadcrumbs';
import {theme} from 'Core/components/WithTheme';
import {constant} from 'fp-ts/lib/function';
import * as NEA from 'fp-ts/lib/NonEmptyArray';
import * as O from 'fp-ts/lib/Option';
import {pipe} from 'fp-ts/lib/pipeable';
import React, {useEffect} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import {Link, useRouteMatch} from 'react-router-dom';
import {ReducerState} from 'rootReducer';
import Column from 'Shared/components/PagedView/Column';
import {
  withDateFilterProperties,
  withSelectFilterProperties,
  withStringFilterProperties,
} from 'Shared/components/PagedView/Filter';
import PagedView from 'Shared/components/PagedView/PagedView';
import withSelectedAgency from 'Shared/components/withSelectedAgency';
import {AgencyFilters} from 'Shared/models/AgencyFilters';
import {toFormattedDateAsUTC} from 'Shared/utilities/DateFormatter';
import {toMoneyValue} from 'Shared/utilities/FormatMoney';
import {prop} from 'Shared/utilities/prop';
import {withValueChangedHook} from 'Shared/utilities/withValueChanged';
import {selectCurrentAgentNumbers} from 'Users/selectors/userProfileSelectors';
import FileDownloadButton, {FileExtension} from 'Shared/components/FileDownloadButton';
import {addFilterParameters} from 'Shared/utilities/addFilterParameters';
import FileDownloadFab from 'Shared/components/FileDownloadFab';

type PolicyApplicationsProps = {
  selectedAgency: AgencyFilters;
};

const Applications = (props: PolicyApplicationsProps) => {
  const {selectedAgency} = props;
  const dispatch = useDispatch();
  const match = useRouteMatch();
  useBreadcrumbs('Policy Applications', match.url);
  const agentNumbers = useSelector(selectCurrentAgentNumbers);
  const {applications, sort, filters} = useSelector((state: ReducerState) => state.policyApplications);

  const changeFilter = withValueChangedHook(filters, changeApplicationsFilter, dispatch);
  const filterProperties = {
    changeFilter,
    filterData: filters,
  };
  const additionalFilters = {
    isNewYork: pipe(agentNumbers, NEA.fromArray, O.map(NEA.head), O.fold(constant(false), prop('isNewYork'))),
    agentNumbers: agentNumbers.map(prop('value')),
  };

  const statuses: FilterEnum[] = [
    {
      name: 'Submitted',
      value: '1',
    },
    {
      name: 'Pending',
      value: '2',
    },
    {
      name: 'Processed',
      value: '3',
    },
  ];

  const sources: FilterEnum[] = [
    {
      name: 'AllApp',
      value: '1',
    },
    {
      name: 'EMW',
      value: '2',
    },
  ];

  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const exportToCsvProps = {
    startIcon: <i className="fa fa-2x fa-file-download" />,
    extension: FileExtension.CSV,
    downloadedFileName: 'FilteredPoliciesApplications',
    relativeFileLocation: `/policies/applications/csv${addFilterParameters({
      ...filters,
      agentNumbers: agentNumbers.map(prop('value')),
      isNewYork: selectedAgency.isNewYork,
      encryptedIdentifier: selectedAgency.encryptedIdentifier,
    })}`,
    children: 'Export To CSV',
  };

  useEffect(() => {
    dispatch(
      getApplications(
        selectedAgency.encryptedIdentifier,
        {...filters, ...additionalFilters},
        undefined,
        O.toUndefined(sort)
      )
    );
  }, [filters, agentNumbers, sort]);

  return (
    <PagedView
      pagedViewKey="applications"
      sort={O.toUndefined(sort)}
      onChangeSort={sort => {
        dispatch(changeSort(sort));
      }}
      data={applications}
      actions={
        isMobile ? (
          <FileDownloadFab variant="extended" color="primary" displayTooltip {...exportToCsvProps} />
        ) : (
          <FileDownloadButton variant="contained" color="primary" displayTooltip {...exportToCsvProps} />
        )
      }
      loadMore={page =>
        dispatch(
          getApplications(
            selectedAgency.encryptedIdentifier,
            {...filters, ...additionalFilters},
            page,
            O.toUndefined(sort)
          )
        )
      }
    >
      {columnProps => (
        <>
          <Column
            {...columnProps}
            title="Account Number"
            sortColumnKey="accountNumber"
            filterProperties={withStringFilterProperties(filterProperties, 'accountNumber')}
            default
          >
            {application => (
              <Link to={AccountRouteConstants.ACCOUNTS_BY_ACCOUNT_NUMBER(application.accountNumber)}>
                <Button color="primary">
                  <Typography variant="body2">{application.accountNumber}</Typography>
                </Button>
              </Link>
            )}
          </Column>
          <Column
            {...columnProps}
            title="Account Name"
            sortColumnKey="employerName"
            filterProperties={withStringFilterProperties(filterProperties, 'employerName')}
            default
          >
            {application => application.employerName}
          </Column>
          <Column
            {...columnProps}
            title="Writing Agent Name"
            sortColumnKey="writingAgent"
            filterProperties={withStringFilterProperties(filterProperties, 'writingAgent')}
            default
          >
            {application => application.writingAgent}
          </Column>
          <Column
            {...columnProps}
            title="Writing Agent Number"
            sortColumnKey="writingAgentNumber"
            filterProperties={withStringFilterProperties(filterProperties, 'writingAgentNumber')}
            default
          >
            {application => application.writingAgentNumber}
          </Column>
          <Column
            {...columnProps}
            title="Insured Name"
            sortColumnKey="insuredName"
            filterProperties={withStringFilterProperties(filterProperties, 'insuredName')}
            default
          >
            {application => application.insuredName}
          </Column>
          <Column
            {...columnProps}
            title="Insured Last 4-SSN"
            sortColumnKey="insuredIdentifier"
            filterProperties={withStringFilterProperties(filterProperties, 'insuredIdentifier')}
            default
          >
            {application => application.insuredIdentifier}
          </Column>
          <Column
            {...columnProps}
            title="Owner Name"
            sortColumnKey="employeeName"
            filterProperties={withStringFilterProperties(filterProperties, 'employeeName')}
            default
          >
            {application => application.employeeName}
          </Column>
          <Column
            {...columnProps}
            title="Owner Last 4-SSN"
            sortColumnKey="employeeIdentifier"
            filterProperties={withStringFilterProperties(filterProperties, 'employeeIdentifier')}
            default
          >
            {application => application.employeeIdentifier}
          </Column>
          <Column
            {...columnProps}
            title="Relationship"
            sortColumnKey="relation"
            filterProperties={withStringFilterProperties(filterProperties, 'relation')}
          >
            {application => application.relation}
          </Column>
          <Column
            {...columnProps}
            title="Plan Name"
            sortColumnKey="planName"
            filterProperties={withStringFilterProperties(filterProperties, 'planName')}
          >
            {application => application.planName}
          </Column>
          <Column
            {...columnProps}
            title="Premium Mode"
            sortColumnKey="premiumMode"
            filterProperties={withStringFilterProperties(filterProperties, 'premiumMode')}
          >
            {application => application.premiumMode}
          </Column>
          <Column {...columnProps} title="Deduction Amout">
            {application => toMoneyValue(application.deductionAmount)}
          </Column>
          <Column {...columnProps} title="Annual Premium">
            {application => toMoneyValue(application.annualDeduction)}
          </Column>

          <Column
            {...columnProps}
            title="Date App Signed"
            sortColumnKey="signatureDate"
            filterProperties={withDateFilterProperties(filterProperties, 'signatureDate')}
          >
            {application => toFormattedDateAsUTC(application.signatureDate)}
          </Column>
          <Column
            {...columnProps}
            title="Submission Date"
            sortColumnKey="dateTransmitted"
            filterProperties={withDateFilterProperties(filterProperties, 'dateTransmitted')}
          >
            {application => toFormattedDateAsUTC(application.dateTransmitted)}
          </Column>
          <Column
            {...columnProps}
            title="Original Date"
            sortColumnKey="originalDate"
            filterProperties={withDateFilterProperties(filterProperties, 'originalDate')}
          >
            {application => toFormattedDateAsUTC(application.originalDate)}
          </Column>

          <Column
            {...columnProps}
            title="Status"
            sortColumnKey="status"
            filterProperties={withSelectFilterProperties(filterProperties, 'statuses', statuses, true)}
          >
            {application => application.status!.name}
          </Column>
          <Column
            {...columnProps}
            title="Source"
            sortColumnKey="sourceType"
            filterProperties={withSelectFilterProperties(filterProperties, 'sourceTypeId', sources)}
          >
            {application => application.sourceType!.name}
          </Column>
        </>
      )}
    </PagedView>
  );
};

export default withSelectedAgency(Applications);
